import { useCallback, useEffect, useRef, useState } from "react";
import { fetchStoreCredentialsAPI } from "../../utils/api/stores";

/**
 * Lazy access to a restaurant's stored credentials. Nothing is requested until `reveal()` is
 * called, and only when `enabled` (the caller's permission). The result is kept in memory for the
 * life of the component and dropped on unmount; it is never written to storage.
 *
 * `reveal()` resolves to the credentials, or `null` when they could not be read (`failed` is set).
 */
export const useStoreCredentials = (storeCode, enabled) => {
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reveal = useCallback(async () => {
    if (!enabled || !storeCode) return null;
    if (credentials) return credentials;
    setLoading(true);
    setFailed(false);
    const data = await fetchStoreCredentialsAPI(localStorage.getItem("auth-token"), storeCode);
    if (mounted.current) {
      setLoading(false);
      if (data) setCredentials(data);
      else setFailed(true);
    }
    return data;
  }, [enabled, storeCode, credentials]);

  // A save changed the stored secrets: forget what was revealed.
  const forget = useCallback(() => setCredentials(null), []);

  return { credentials, loading, failed, reveal, forget };
};
